import { renderWithNavCopilot } from 'quartz-core/client-with-copilot';
import Raven from 'raven-js';
import UIApplication from 'UIComponents/app/UIApplication';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { createBrowserHistory } from 'history';

// I18n
import I18n from 'I18n';
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import registerBasicData from 'I18n/init/register/sync/registerBasicData';
import { initAppTranslations } from 'I18n/init/setup/AppInit';

// Rhumb
import { RhumbProvider } from 'react-rhumb';
import rhumbConfig from '../rhumb-config.yaml';
import historyAdapter from 'react-rhumb/historyAdapterV4';

// Redux
import { Provider } from 'react-redux';
import configureStore from './utils/configureStore';
import { startApp } from './actions/AppActions';
import { getAuth } from './actions/AuthActions';
import { loadExperiments } from './actions/ExperimentActions';

// Configure the router
import { Router } from 'react-router-dom';
import { getLangOverrideFromSignup, getRootUrl, redirectToGlobalHome, redirectToGrowthPlan } from './utils/urls';
import Routes from './components/Routes';
import { getIsEnrolledIntoGrowthPlan } from './utils/growthPlan';
import { fetchIsPlpoEnrolled } from 'setup-guide-api/api/plpoApi';
import SetupGuideDataFetchingClientProvider from 'setup-guide-api/dataFetchingClient/client/SetupGuideDataFetchingClientProvider';

// CMS Route Utils init
import { init as initContentRoutes } from 'ContentUtils/Routes';
import ExperimentExposures from './components/ExperimentExposures';

// Error handler
import UnhandledPromiseRejectionListener from './components/UnhandledPromiseRejectionListener';
import ProOnboardingCoachingTipContainer from './containers/ProOnboardingCoachingTipContainer';
import { GROWTH_PLAN_REMOVAL_GATE } from './constants/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class App {
  constructor(basename) {
    this.basename = basename || getRootUrl();
    this.store = configureStore();
    try {
      const isAcceptanceTest = document.cookie.includes('hs_selenium');
      Raven.setTagsContext({
        isAcceptanceTest
      });
    } catch (_unused) {
      // Ignore error for AT checking
    }
  }

  // Kicks off the app. This is intentionally not in the constructor.
  start() {
    const I18nProvider = createStandardI18nProvider();
    const langAvailable = initAppTranslations([I18nProvider.register(lang), registerBasicData(I18nProvider)]);
    const storeReady = this.startRedux();
    Promise.all([getIsEnrolledIntoGrowthPlan(), fetchIsPlpoEnrolled(), ...storeReady]).then(([isEnrolledIntoGrowthPlan, isPlpoEnrolled]) => {
      const {
        auth
      } = this.store.getState();
      const {
        portal,
        user,
        gates
      } = auth;
      if (isPlpoEnrolled) {
        redirectToGlobalHome();
        return;
      }
      const isUngatedForGrowthPlanRemoval = gates.includes(GROWTH_PLAN_REMOVAL_GATE);
      // For users in the acq0143 / coac0001 / coac0002 variant, redirect to Growth Plan app immediately
      if (isEnrolledIntoGrowthPlan && !isUngatedForGrowthPlanRemoval) {
        redirectToGrowthPlan();
        return;
      }

      // For non-en users from signup (with EU hublet) there is an interim period where the user's language
      // has not been replicated on both hubs, hence they would see the site in English until they refresh the page.
      // This change uses the language param we get from signup to initialise i18n (only for new users from signup)
      const langOverrideFromSignup = getLangOverrideFromSignup();
      I18nProvider.setLocale({
        timezone: portal.timezone,
        locale: langOverrideFromSignup || user.locale,
        langEnabled: langOverrideFromSignup ? true : user.lang_enabled
      });
      langAvailable.then(() => {
        document.title = I18n.text('app.htmlTitle');
        initContentRoutes(auth.gates);
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'setUser' does not exist on type 'RavenSt... Remove this comment to see the full error message
        Raven.setUser({
          email: auth.user.email,
          id: auth.user.user_id,
          username: auth.user.email
        });
        this.store.dispatch(startApp());
        this.startRouter();
      }).catch(error => {
        Raven.captureException(error);
      });
    }).catch(error => {
      Raven.captureException(error);
    });
  }
  startRedux() {
    this.store = configureStore();
    const authReady = this.store.dispatch(getAuth());
    const experimentsReady = this.store.dispatch(loadExperiments());
    return [authReady, experimentsReady];
  }
  startRouter() {
    const history = createBrowserHistory({
      basename: this.basename
    });
    renderWithNavCopilot( /*#__PURE__*/_jsxs(UIApplication, {
      name: "setup-guide-ui",
      children: [/*#__PURE__*/_jsx(SetupGuideDataFetchingClientProvider, {
        children: /*#__PURE__*/_jsx(Provider, {
          store: this.store,
          children: /*#__PURE__*/_jsx(RhumbProvider
          // @ts-expect-error ts-migrate(2322) FIXME: Type 'unknown' is not assignable to type 'PartialR... Remove this comment to see the full error message
          , {
            config: rhumbConfig,
            history: historyAdapter(history),
            trackVisibility: true,
            children: /*#__PURE__*/_jsxs(Router, {
              history: history,
              children: [/*#__PURE__*/_jsx(Routes, {}), /*#__PURE__*/_jsx(ExperimentExposures, {}), /*#__PURE__*/_jsx(ProOnboardingCoachingTipContainer, {})]
            })
          })
        })
      }), /*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(UnhandledPromiseRejectionListener, {})]
    }), {
      layoutType: 'sidebar'
    });
  }
}